import * as Sentry from '@sentry/browser'

export default defineNuxtPlugin(() => {
  const userStore = useUserStore()
  const { ensureUserServices } = userStore
  const { token } = storeToRefs(userStore)
  const callStore = useCallStore()
  const { canUseVoiceCalls } = useFeatureFlags()
  const { connect, disconnect } = useCallEventStream()

  let disposed = false

  // Unconditional: only removeDevice() cancels a retry that is already scheduled.
  const teardown = async () => {
    disconnect()
    await callStore.removeDevice()
  }

  const loadTenants = async () => {
    try {
      await ensureUserServices()
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  // Stamped and queued: overlapping runs tear down a device a later one already set up.
  let generation = 0
  let queue: Promise<void> = Promise.resolve()

  const apply = async (isAuthenticated: boolean, canCall: boolean, stamp: number) => {
    try {
      if (disposed || stamp !== generation) { return }
      if (!isAuthenticated) {
        await teardown()
        return
      }
      if (!canCall) {
        // Not awaited: this watcher runs again when the flags land. Must stay behind the
        // isAuthenticated guard, because unauthenticated it poisons cachedUserServices.
        loadTenants()
        await teardown()
        return
      }
      await callStore.setCallDevice()
      // The newer run applies the right state; tearing down here would drop a live call.
      if (disposed || stamp !== generation) { return }
      connect()
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  // Whether there is a token, not which one: connect() reads it itself, and the two-minute refresh
  // rotates it in every tab, so watching the value re-registered the device once per tab per turn.
  const stopWatching = watch(
    [() => !!token.value, () => canUseVoiceCalls.value],
    ([isAuthenticated, canCall]) => {
      const stamp = ++generation
      queue = queue.then(() => apply(isAuthenticated, canCall, stamp))
    },
    { immediate: true }
  )

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      disposed = true
      stopWatching()
      disconnect()
    })
  }
})
